// frontend/src/components/NotificationDetailPage.tsx
//
// Phase 3.5 (#211): Dedicated read page for an admin broadcast.
//
// Reached when:
// - User clicks a broadcast in the bell that has no deep_link (Header.tsx routes them here)
// - Anyone navigates to /notifications/<uuid> directly
//
// Lifecycle:
// 1. Fetch the broadcast list, find the one matching :id
// 2. Render full message with category styling
// 3. User clicks "Acknowledge" → POST /click → navigate back
//    (For dismissable categories, label is "Acknowledge & dismiss".
//     For click-only, just "Acknowledge" — semantics are the same since
//     click IS the dismiss for click-only categories.)
// 4. User clicks "Close" → navigate back without dismissing (broadcast stays in bell)
//
// Navigation back: useNavigate(-1) returns to wherever the user was when they
// opened the bell. Falls back to /trips if there's no history (direct URL hit).

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  Campaign as CampaignIcon,
  NewReleases as NewReleasesIcon,
  FlightTakeoff as FlightTakeoffIcon,
} from '@mui/icons-material';
import { api, clickNotification } from '../utils/api';
import { getCategoryMeta } from '../utils/notificationCategories';
import type { AdminBroadcast } from '../hooks/useNudges';

const ICON_BY_NAME: Record<string, React.ComponentType<any>> = {
  Security: SecurityIcon,
  Build: BuildIcon,
  Warning: WarningIcon,
  Campaign: CampaignIcon,
  NewReleases: NewReleasesIcon,
  FlightTakeoff: FlightTakeoffIcon,
};

const formatTimestamp = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'long',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const NotificationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [broadcast, setBroadcast] = useState<AdminBroadcast | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [acknowledging, setAcknowledging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Whether the user navigated here vs landed by direct URL.
  // location.key === 'default' means React Router's initial entry — no back history.
  const hasHistory = location.key !== 'default';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get('/users/me/nudges');
        const data = await response.json();
        if (cancelled) return;

        if (!response.ok || !data.success) {
          setError(data.error || 'Failed to load notification');
          setLoading(false);
          return;
        }

        const found = (data.admin_broadcasts || []).find(
          (b: AdminBroadcast) => b.notification_id === id
        );

        if (!found) {
          // Either it doesn't exist, has been recalled, or the user already
          // dismissed it. From the user's perspective, all three are "not in
          // your active list anymore" — the page just shouldn't show stale
          // content for something they've moved past.
          setNotFound(true);
        } else {
          setBroadcast(found);
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError('Network error loading notification');
        setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id]);

  const goBack = () => {
    if (hasHistory) {
      navigate(-1);
    } else {
      navigate('/trips');
    }
  };

  const handleAcknowledge = async () => {
    if (!broadcast) return;
    setAcknowledging(true);
    try {
      await clickNotification(broadcast.notification_id);
    } catch (err) {
      // Non-fatal: user can re-acknowledge from the bell next time the page
      // refreshes. Don't block their navigation away.
      console.warn('clickNotification failed:', err);
    }
    goBack();
  };

  // ─── Loading state ────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ─── Error state ──────────────────────────────────────────
  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6, px: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} variant="outlined">
          Back
        </Button>
      </Box>
    );
  }

  // ─── Not found / already dismissed / recalled ─────────────
  if (notFound) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6, px: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          This notification is no longer active. It may have been dismissed,
          recalled, or expired.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} variant="outlined">
          Back
        </Button>
      </Box>
    );
  }

  // ─── Active broadcast ─────────────────────────────────────
  // broadcast is non-null here per the not-found check above
  const meta = getCategoryMeta(broadcast!.category);
  const Icon = ICON_BY_NAME[meta.iconName] ?? CampaignIcon;
  const acknowledgeLabel = meta.dismissable ? 'Acknowledge & dismiss' : 'Acknowledge';

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, px: 3, pb: 6 }}>
      {/* Back link */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={goBack}
        sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
      >
        Back
      </Button>

      <Paper
        elevation={2}
        sx={{
          borderLeft: `6px solid ${meta.color}`,
          p: 4,
        }}
      >
        {/* Category label + icon */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Icon sx={{ color: meta.color, fontSize: 28 }} />
          <Typography
            variant="overline"
            sx={{
              color: meta.color,
              fontWeight: 700,
              letterSpacing: '1px',
              fontSize: '0.8rem',
            }}
          >
            {meta.label}
          </Typography>
        </Stack>

        {/* Message body — preserved as-is, no truncation */}
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '1.05rem',
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          {broadcast!.message}
        </Typography>

        {/* Sent timestamp + severity hint */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 4 }}>
          Posted {formatTimestamp(broadcast!.sent_at)}
          {broadcast!.severity === 'warning' && ' · marked as warning'}
        </Typography>

        {/* Action buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleAcknowledge}
            disabled={acknowledging}
            sx={{
              bgcolor: meta.color,
              '&:hover': { bgcolor: meta.color, opacity: 0.9 },
            }}
          >
            {acknowledging ? 'Acknowledging…' : acknowledgeLabel}
          </Button>
          <Button
            variant="outlined"
            onClick={goBack}
            disabled={acknowledging}
          >
            Close
          </Button>
        </Stack>

        {!meta.dismissable && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}
          >
            This is an important {meta.label.toLowerCase()} notice. Acknowledging
            confirms you have read it.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default NotificationDetailPage;
